import type { AxiosInstance } from "axios";
import {
  AI_SPREADSHEET_OPERATION_STATUS,
  AcceptedAiSpreadsheetOperationResponse,
  AiSpreadsheetOperation,
} from "@/payroll/ai-script/types";
import {
  WorkbookOperationFailedError,
  WorkbookOperationPollingError,
  getWorkbookOperationPath,
  isOperation,
  operationFromConflict,
  pollOperation,
  wait,
} from "./poll";
import {
  type WorkbookOperationRunResult,
  clearPersistedOperation,
  controllers,
  getSnapshot,
  persistOperation,
  readPersistedWorkbookOperation,
  scopeKey,
  setSnapshot,
} from "./store";

const CONFLICT_RESUBMIT_DELAYS_MS = [250, 500, 1_000, 2_000];

const CONFLICT_RETRY_MESSAGE =
  "Another Grid recalculation is still clearing. Retry this edit in a moment.";

export const runWorkbookOperation = async ({
  api,
  businessId,
  runId,
  submit,
}: {
  api: AxiosInstance;
  businessId: string;
  runId: string;
  submit: () => Promise<AcceptedAiSpreadsheetOperationResponse>;
}): Promise<WorkbookOperationRunResult> => {
  const key = scopeKey(businessId, runId);
  controllers.get(key)?.abort();
  const controller = new AbortController();
  controllers.set(key, controller);

  let operation: AiSpreadsheetOperation;
  let adoptedConflict = false;
  try {
    const accepted = await submit();
    if (!isOperation(accepted.operation)) {
      throw new Error("The Grid edit response was not recognized.");
    }
    operation = accepted.operation;
  } catch (error) {
    const conflictingOperation = operationFromConflict(error);
    if (!conflictingOperation) {
      if (controllers.get(key) === controller) controllers.delete(key);
      throw error;
    }
    operation = conflictingOperation;
    adoptedConflict = true;
  }

  setSnapshot(key, {
    operation,
    isPending: true,
    errorMessage: null,
    retry: null,
  });
  persistOperation(businessId, runId, operation);

  try {
    const terminal = await pollOperation(
      api,
      businessId,
      runId,
      operation,
      controller.signal,
    );
    if (terminal.status === AI_SPREADSHEET_OPERATION_STATUS.failed) {
      clearPersistedOperation(businessId, runId);
      if (adoptedConflict) {
        return { operation: terminal, adoptedConflict };
      }

      setSnapshot(key, {
        operation: terminal,
        isPending: false,
        errorMessage: terminal.error?.message ?? "Grid recalculation failed.",
        retry: null,
      });
      throw new WorkbookOperationFailedError(terminal);
    }
    return { operation: terminal, adoptedConflict };
  } catch (error) {
    if (
      error instanceof WorkbookOperationFailedError ||
      controller.signal.aborted
    ) {
      throw error;
    }

    const latestOperation = getSnapshot(key).operation ?? operation;
    const message =
      error instanceof Error
        ? error.message
        : "Grid recalculation status could not be refreshed.";
    setSnapshot(key, {
      operation: latestOperation,
      isPending: false,
      errorMessage: message,
      retry: null,
    });
    throw new WorkbookOperationPollingError(latestOperation, message);
  } finally {
    if (controllers.get(key) === controller) controllers.delete(key);
  }
};

export const runWorkbookOperationThroughConflicts = async (args: {
  api: AxiosInstance;
  businessId: string;
  runId: string;
  submit: () => Promise<AcceptedAiSpreadsheetOperationResponse>;
  signal?: AbortSignal;
}) => {
  let result = await runWorkbookOperation(args);
  const signal = args.signal ?? new AbortController().signal;

  for (const delay of CONFLICT_RESUBMIT_DELAYS_MS) {
    if (!result.adoptedConflict) return result.operation;

    await wait(delay, signal);
    result = await runWorkbookOperation(args);
  }

  if (result.adoptedConflict) {
    clearPersistedOperation(args.businessId, args.runId);
    const key = scopeKey(args.businessId, args.runId);
    setSnapshot(key, {
      operation: result.operation,
      isPending: false,
      errorMessage: CONFLICT_RETRY_MESSAGE,
      retry: null,
    });
    throw new WorkbookOperationPollingError(
      result.operation,
      CONFLICT_RETRY_MESSAGE,
    );
  }

  return result.operation;
};

export const recoverWorkbookOperation = async (
  api: AxiosInstance,
  businessId: string,
  runId: string,
  signal?: AbortSignal,
) => {
  const persisted = readPersistedWorkbookOperation(businessId, runId);
  if (!persisted) return null;

  const response = await api.get<{ operation: AiSpreadsheetOperation }>(
    getWorkbookOperationPath(businessId, runId, persisted.operationId),
    { signal },
  );
  if (signal?.aborted) {
    throw new DOMException("Workbook recovery stopped", "AbortError");
  }
  if (!isOperation(response.data.operation)) {
    clearPersistedOperation(businessId, runId);
    throw new Error("The Grid operation response was not recognized.");
  }

  return runWorkbookOperation({
    api,
    businessId,
    runId,
    submit: async () => ({
      operation: response.data.operation,
      idempotent_replay: true,
    }),
  });
};
